// UI-only per-status counters for the mail queue. Each chip doubles as a
// one-click status filter: clicking "failed 3" filters the table to failed
// letters. Counts are GLOBAL (independent of the active search/kind/period
// filters — the same numbers the admin dashboard shows), which is exactly what
// an operator needs while triaging: the queue's overall shape never disappears.
import type { ReactElement } from 'react';
import { Card, Skeleton, Typography } from 'antd';
import { useTranslation } from '@govard/i18n-core/react';

import type { OutboxStatus } from '../api/admin.client.js';

import { OutboxStatusBadge } from './OutboxStatusBadge.js';

const { Text } = Typography;

export interface OutboxStatusSummaryProps {
  counts: Array<{ status: OutboxStatus; count: number }> | undefined;
  loading: boolean;
  activeStatus: 'all' | OutboxStatus;
  onSelect: (status: 'all' | OutboxStatus) => void;
}

export function OutboxStatusSummary({
  counts,
  loading,
  activeStatus,
  onSelect,
}: OutboxStatusSummaryProps): ReactElement {
  const { t } = useTranslation();

  if (loading) {
    return (
      <Card size="small" data-testid="admin-mail-status-summary">
        <Skeleton active paragraph={{ rows: 0 }} />
      </Card>
    );
  }

  const total = (counts ?? []).reduce((sum, row) => sum + row.count, 0);

  function chip(key: 'all' | OutboxStatus, label: ReactElement, count: number): ReactElement {
    const active = activeStatus === key;
    return (
      <button
        key={key}
        type="button"
        data-testid={`admin-mail-status-chip-${key}`}
        aria-pressed={active}
        onClick={() => onSelect(key)}
        className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 transition-colors"
        style={{
          border: `1px solid ${active ? 'var(--ant-color-primary)' : 'var(--ant-color-border)'}`,
          cursor: 'pointer',
          background: active ? 'var(--ant-color-primary-bg)' : 'transparent',
        }}
      >
        {label}
        <Text className="font-mono font-semibold" style={{ fontSize: 12 }}>
          {count}
        </Text>
      </button>
    );
  }

  return (
    <Card size="small" data-testid="admin-mail-status-summary" styles={{ body: { padding: 12 } }}>
      <div className="flex flex-wrap items-center gap-2">
        {chip('all', <Text style={{ fontSize: 11 }}>{t('crm.admin.mail.filter_all')}</Text>, total)}
        {(counts ?? []).map((row) =>
          chip(row.status, <OutboxStatusBadge status={row.status} />, row.count),
        )}
      </div>
    </Card>
  );
}
